import { useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function ForgotPassword() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devCode, setDevCode] = useState('');

  async function handleRequestCode(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.devCode) setDevCode(data.devCode);
      setStep(2);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) return setError('Passwords do not match');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Reset failed');
      else setStep(3);
    } catch (err) {
      setError('Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
      <div className="mb-8 text-center">
        <h1 className="text-5xl font-black text-yellow-400" style={{ fontFamily: 'Bebas Neue, Impact, sans-serif' }}>STARFUEL</h1>
        <p className="text-gray-500 text-sm">CarFleet & Chauffeur Service</p>
      </div>
      <div className="w-full max-w-md bg-gray-900 rounded-2xl border border-gray-800 p-8">
        {step === 3 && (
          <div className="text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-white mb-2">Password Reset!</h2>
            <p className="text-gray-400 mb-6">Your password has been updated. You can now sign in.</p>
            <Link to="/login" className="block w-full py-3.5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition text-center">Sign In →</Link>
          </div>
        )}
        {step === 1 && (
          <>
            <h2 className="text-2xl font-bold text-white mb-2">Forgot Password</h2>
            <p className="text-gray-400 text-sm mb-6">Enter your email and we will send you a reset code.</p>
            <form onSubmit={handleRequestCode} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition"
                  placeholder="you@restaurant.com" />
              </div>
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">{error}</div>}
              <button type="submit" disabled={loading} className="w-full py-3.5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition disabled:opacity-50">
                {loading ? 'Sending...' : 'Send Reset Code →'}
              </button>
            </form>
          </>
        )}
        {step === 2 && (
          <>
            <h2 className="text-2xl font-bold text-white mb-2">Enter Reset Code</h2>
            <p className="text-gray-400 text-sm mb-2">A 6-digit code was sent to <span className="text-white">{email}</span></p>
            {devCode && (
              <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-xl px-4 py-3 mb-4">
                <p className="text-xs text-yellow-400 font-mono">Dev mode — your code: <strong>{devCode}</strong></p>
                <p className="text-xs text-gray-500 mt-1">In production this will be sent by email</p>
              </div>
            )}
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">6-Digit Code</label>
                <input type="text" value={code} onChange={e => setCode(e.target.value)} required maxLength={6}
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition font-mono text-center text-2xl tracking-widest"
                  placeholder="000000" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">New Password</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={8}
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition"
                  placeholder="Min 8 chars, include a number" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Confirm Password</label>
                <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400 transition"
                  placeholder="Repeat your new password" />
              </div>
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">{error}</div>}
              <button type="submit" disabled={loading} className="w-full py-3.5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition disabled:opacity-50">
                {loading ? 'Resetting...' : 'Reset Password →'}
              </button>
            </form>
          </>
        )}
        <p className="mt-5 text-center text-gray-400 text-sm">
          Remember it? <Link to="/login" className="text-yellow-400 hover:text-yellow-300 font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
